/**
 * The still a film cover is published with.
 *
 * A cover is shown *without being played* in more places than it is played — a feed
 * card, a message, a drafts row, a share — and a book whose cover is a film used to
 * have no picture of itself anywhere: the record said the cover was a film and which
 * film, and no field held a frame of it. So publishing derives one from the film and
 * writes it onto the cover, and every reader — including one on a device that cannot
 * decode this film at all — gets the picture from the record instead.
 *
 * Three decisions live here, and each is one a creator would notice:
 *
 *  - **Which instant.** The frame at the moment the cover *opens* (`videoStartMs`),
 *    because the still is what a reader meets before the cover plays. Reading the
 *    film's head instead would make the picture and the film disagree about which
 *    minute the creator chose.
 *  - **Best effort.** A film that will not decode, or one served without the headers
 *    a canvas needs, yields nothing — the book is published without a still, which is
 *    a plain card, not a failed publish.
 *  - **Bounded.** A still is a decoration and a save is not, so the read is raced
 *    against a budget: a film still dragging itself down the wire must not hold a
 *    creator's publish open. The read is left to finish on its own — the reader keeps
 *    the element, so a card asking for the same film later pays nothing.
 *
 * The reading itself is the platform's, through `#/lib/flashbookFilmFrames`, so this
 * module holds only the policy and can be reasoned about — and tested — without a
 * browser or a device.
 */
import {readFilmStill} from '#/lib/flashbookFilmFrames'

/**
 * How long a save will wait for a film's still before going on without one.
 *
 * Longer than a film already on the device takes to give up a frame, and shorter
 * than a person's patience with a spinner.
 */
export const FILM_STILL_BUDGET_MS = 6000

/** The part of a cover this rule has an opinion about. */
export type FilmCoverLike = {
  kind: 'image' | 'audio' | 'video'
  videoUri?: string
  /** Which minute of the film the cover opens on (see `#/lib/coverTrim`). */
  videoStartMs?: number
}

/**
 * One frame of a film cover, as a URL an `<Image>` can show.
 *
 * `undefined` for a cover that is not a film, for a film that yielded nothing, and
 * for one that did not answer in time — all three are "publish it without a still".
 */
export async function filmCoverStill(
  cover: FilmCoverLike,
  budgetMs: number = FILM_STILL_BUDGET_MS,
): Promise<string | undefined> {
  if (cover.kind !== 'video' || !cover.videoUri) return undefined

  const read = readFilmStill(cover.videoUri, cover.videoStartMs ?? 0)
  return await new Promise<string | undefined>(resolve => {
    /*
     * The timer is cleared when the read wins, rather than left to fire into a
     * promise nobody is waiting on: a save is a moment in a studio that goes on
     * doing things, and a budget that outlives it is one more thing running under
     * a screen the creator has left.
     */
    const timer = setTimeout(() => resolve(undefined), budgetMs)
    const settle = (value: string | undefined) => {
      clearTimeout(timer)
      resolve(value)
    }
    read.then(settle, () => settle(undefined))
  })
}
